import { useState, useEffect } from "react";
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, Switch, TextField, Alert, Typography } from "@mui/material";
import { updateAdvisor } from "../services/gutcheck-admin-api.service";
import { useAuth0 } from "@auth0/auth0-react";
import { IAdvisor } from "../../../types";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";

export const OnboardingTab = ({
    advisor,
    onUpdate,
    onFieldUpdate
}: {
    advisor: IAdvisor,
    onUpdate: () => void,
    onFieldUpdate?: (updates: Partial<IAdvisor>) => void
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        internal_notes: advisor.internal_notes || "",
        in_icp: advisor.in_icp || false,
        intro_enabled: advisor.intro_enabled || false,
        preview: advisor.preview || false
    });

    // Auto-clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Update form data when advisor prop changes
    useEffect(() => {
        setFormData({
            internal_notes: advisor.internal_notes || "",
            in_icp: advisor.in_icp || false,
            intro_enabled: advisor.intro_enabled || false,
            preview: advisor.preview || false
        });
    }, [advisor]);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        if (!advisor.survey_link?.project?.id) {
            setErrorMessage("Project ID is required");
            setIsSubmitting(false);
            return;
        }

        try {
            const accessToken = await getAccessTokenSilently();
            await updateAdvisor(
                advisor.survey_link.project.id,
                advisor.id,
                {
                    internal_notes: formData.internal_notes,
                    in_icp: formData.in_icp,
                    intro_enabled: formData.intro_enabled,
                    preview: formData.preview
                },
                accessToken
            );
            setSuccessMessage("Onboarding settings updated successfully");
            // Update the local advisor state to reflect the saved changes
            // This avoids triggering a full reload while keeping the data current
            if (onFieldUpdate) {
                onFieldUpdate({
                    internal_notes: formData.internal_notes,
                    in_icp: formData.in_icp,
                    intro_enabled: formData.intro_enabled,
                    preview: formData.preview
                });
            }
        } catch (error) {
            setErrorMessage("An error occurred while updating onboarding settings");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>

            <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3} sx={{ marginTop: '20px' }}>
                    <TextField
                        label="Internal Notes"
                        multiline
                        minRows={4}
                        value={formData.internal_notes}
                        onChange={(e) => handleChange('internal_notes', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Add internal notes about this advisor..."
                    />

                    <DisplayBoxBase className="column white left" sx={{ gap: "10px" }}>
                        <strong>Onboarding Track</strong>
                        <Typography variant="body1">
                            Control whether this advisor is prompted to setup a Founder Intro and Product Demo during onboarding or if they should move straight into Nurture.
                        </Typography>
                        <Typography variant="body1">
                            They should only be included in the Founder Intro track if:
                        </Typography>
                        {advisor.survey_link?.project?.intro_criteria ? (
                            <ul>
                                {advisor.survey_link?.project?.intro_criteria.split("\n").map((criteria, index) => (
                                    <li key={index}>{criteria}</li>
                                ))}
                            </ul>
                        ) : (
                            <ul>
                                <li>They are clearly in the ICP of the company</li>
                                <li>They are clearly the decision maker for this type of product</li>
                                <li>They fit within the guidelines the company has provided for the Founder Intro track</li>
                            </ul>
                        )}

                        <TextField
                            label="Onboarding Track"
                            select
                            value={formData.intro_enabled}
                            onChange={(e) => handleChange('intro_enabled', e.target.value === "true")}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="true">Founder Intro</MenuItem>
                            <MenuItem value="false">Nurture</MenuItem>
                        </TextField>
                    </DisplayBoxBase>

                    <DisplayBoxBase className="column white left" sx={{ gap: "10px" }}>
                        <strong>Preview to Company</strong>
                        <Typography variant="body1">
                            If enabled, this advisor will show up as "In Progress" on the company's dashboard. Make sure their profile is updated to show their picture, full name, title, and company logo.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.preview}
                                    onChange={(e) => handleChange('preview', e.target.checked)}
                                />
                            }
                            label="Preview to Company"
                        />
                    </DisplayBoxBase>



                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2, alignItems: 'center' }}>
                        {successMessage && <Typography variant="body1" color="success.main">{successMessage}</Typography>}
                        {errorMessage && <Typography variant="body1" color="error.main">{errorMessage}</Typography>}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            Save Onboarding Settings
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};